import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Mail, Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import FreeTenantHelp from "@/components/FreeTenantHelp";

const SUPPORT_EMAIL = "support@tenanttrail.net";

export default function Support() {
 const [name, setName] = useState("");
 const [email, setEmail] = useState("");
 const [topic, setTopic] = useState("general");
 const [message, setMessage] = useState("");
 const [sending, setSending] = useState(false);
 const [sent, setSent] = useState(false);
 const [error, setError] = useState("");

 const submit = async (e) => {
 e?.preventDefault();
 setError("");
 if (!email || !email.includes("@")) { setError("Enter a valid email so we can reply."); return; }
 if (!message || message.trim().length < 20) { setError("Please give us a few more details (at least 20 characters)."); return; }
 setSending(true);
 try {
 await axios.post(`${API}/support/contact`, {
 name: name.trim(),
 email: email.trim().toLowerCase(),
 topic,
 message: message.trim(),
 });
 setSent(true);
 } catch (err) {
 setError(err.response?.data?.detail || "Couldn't send right now. Try emailing us directly.");
 } finally {
 setSending(false);
 }
 };

 return (
 <div className="min-h-screen bg-[#FAF7F0]">
 <Navbar />

 {/* Free state services first — most people who click Support have an
     urgent tenancy problem. Give them the free authoritative option
     before showing them our contact form. */}
 <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 pt-12 sm:pt-16" data-testid="support-free-help">
 <FreeTenantHelp
 variant="full"
 heading="Free help in your state, right now."
 subheading="If your matter is urgent or you're heading to a tribunal, these government-funded services will help you at no cost. Contact them first, then come back to us for anything else."
 />
 </section>

 <main className="max-w-2xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16" data-testid="support-page">
 <div className="text-center mb-10">
 <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-[#1B6C62]/30 mb-4">
 <Mail size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="label-mono text-[#1B6C62]">TenantTrail Support</span>
 </div>
 <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] mb-3">We&apos;re here to help</h1>
 <p className="text-[#6B7280] font-serif leading-relaxed">
 For questions about your TenantTrail account, billing or a bug, email us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#1B6C62] underline">{SUPPORT_EMAIL}</a> or use the form below. We reply within one business day.
 </p>
 </div>

 {sent ? (
 <div className="bg-white border border-[#1B6C62]/30 p-8 text-center" data-testid="support-sent">
 <CheckCircle2 size={44} strokeWidth={1.5} className="mx-auto text-[#1B6C62] mb-4" />
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-2">Thanks. Message received</h2>
 <p className="text-[#6B7280] font-serif">We&apos;ll get back to you at <strong>{email}</strong> within one business day.</p>
 <Link to="/" className="btn-ghost text-sm inline-block mt-6">Back to home</Link>
 </div>
 ) : (
 <form onSubmit={submit} className="bg-white border border-[#E5E4DE] p-6 sm:p-8 space-y-4" data-testid="support-form">
 <div className="grid sm:grid-cols-2 gap-4">
 <div>
 <label className="label-mono block mb-2">Your name</label>
 <input
 type="text"
 value={name}
 onChange={(e) => setName(e.target.value)}
 placeholder="Optional"
 className="input-field"
 data-testid="support-name-input"
 />
 </div>
 <div>
 <label className="label-mono block mb-2">Your email</label>
 <input
 type="email"
 value={email}
 onChange={(e) => setEmail(e.target.value)}
 placeholder="you@example.com"
 className="input-field"
 required
 data-testid="support-email-input"
 />
 </div>
 </div>
 <div>
 <label className="label-mono block mb-2">What can we help with?</label>
 <select value={topic} onChange={(e) => setTopic(e.target.value)} className="input-field" data-testid="support-topic-select">
 <option value="general">General question</option>
 <option value="billing">Billing or refund</option>
 <option value="account">My account / login</option>
 <option value="bug">Something isn&apos;t working</option>
 <option value="legal">Privacy or data request</option>
 <option value="press">Press / partnerships</option>
 </select>
 </div>
 <div>
 <label className="label-mono block mb-2">Your message</label>
 <textarea
 value={message}
 onChange={(e) => setMessage(e.target.value)}
 rows={6}
 placeholder="Give us as much detail as you can…"
 className="input-field resize-y"
 required
 data-testid="support-message-input"
 />
 </div>
 {error && (
 <div className="text-sm text-[#B35D4C] flex items-center gap-2" data-testid="support-error">
 <AlertCircle size={14} strokeWidth={1.75} /> {error}
 </div>
 )}
 <button
 type="submit"
 disabled={sending}
 className="btn-primary w-full inline-flex items-center justify-center gap-2"
 data-testid="support-submit"
 >
 {sending ? (
 <><Loader2 size={16} className="animate-spin" /> Sending…</>
 ) : (
 <><Send size={16} strokeWidth={1.75} /> Send message</>
 )}
 </button>
 <p className="text-[11px] text-[#6B7280] font-serif italic text-center leading-relaxed">
 By submitting you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link>.
 </p>
 </form>
 )}
 
      <SiteFooter />
      </main>
 </div>
 );
}
